import React from "react";
import ReactDOM from "react-dom/client";
import "vercel-toast/css";
import { ConfigCatProvider } from "configcat-react";
import { BrowserRouter } from "react-router-dom";
import { Auth0Provider } from "@auth0/auth0-react";
import { QueryClient, QueryClientProvider } from "react-query";
import "@radix-ui/themes/styles.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { params } from "./params";
import { GeistContainer } from "./components/geistContainer";
import { QuackTheme } from "./components/Theme";
import { FeatureAccessProvider } from "./context/featureAccess";
import * as Sentry from "@sentry/react";
import "./index.css";
import "./theme.scss";
import "./styles/overrides.css";
import { ApiProvider } from "./api/useApi";
import { FlagOverridesProvider } from "@/v2/components/feature-flag/FeatureFlagContext";
import ErrorBoundary from "./v2/components/error-boundary/ErrorBoundary";
import { TraceProvider } from "./context/trace";
import { beforeSend } from "./utils/sentry";

Sentry.init({
  dsn: "https://d429c60c36a2781d37bdd1aec6efe583@o4506401607909376.ingest.us.sentry.io/4508552236957696",
  initialScope: {
    tags: {
      service: "webapp",
    },
  },
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Tracing
  tracesSampleRate: 1.0,
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  // tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
  tracePropagationTargets: [
    params.SDK_URL,
    params.API_URL,
    params.GRAPHQL_URL,
    params.AI_URL,
    params.SHARED_REPORT_URL,
  ],
  // Session Replay
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  enabled: params.SENTRY_ENABLED,
  environment: params.SENTRY_ENVIRONMENT,
  beforeSend,
});

const queryClient = new QueryClient();

const getSubdomain = () => {
  const host = window.location.host;
  const parts = host.split(".");
  if (parts.length > 2) {
    return parts[0];
  }
  return null;
};

const subdomain = getSubdomain();
let organizationId = "";
if (subdomain === "global-e") {
  organizationId = "org_qOK3qqQpXsvFOBtF";
} else if (subdomain === "dremio") {
  organizationId = "org_f1Itw3fS9SbPZcTY";
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

root.render(
  <React.StrictMode>
    <ConfigCatProvider sdkKey={params.FEATURE_FLAG_ID}>
      <Auth0Provider
        domain={params.AUTH0_DOMAIN}
        clientId={params.CLIENT_ID}
        authorizationParams={
          organizationId
            ? {
              redirect_uri: window.location.origin,
              organization: organizationId,
            }
            : {
              redirect_uri: window.location.origin,
            }
        }
      >
        <ErrorBoundary>
          <FlagOverridesProvider>
            <QueryClientProvider client={queryClient}>
              <FeatureAccessProvider>
                <TraceProvider>
                  <ApiProvider>
                    <QuackTheme>
                      <GeistContainer>
                        <BrowserRouter>
                          <App />
                        </BrowserRouter>
                      </GeistContainer>
                    </QuackTheme>
                  </ApiProvider>
                </TraceProvider>
              </FeatureAccessProvider>
            </QueryClientProvider>
          </FlagOverridesProvider>
        </ErrorBoundary>
      </Auth0Provider>
    </ConfigCatProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
